import "../style.scss";
import { useModalState } from "@clipboard-health/ui-react";
import { isDefined } from "@clipboard-health/util-ts";
import {
  IonButton,
  IonChip,
  IonCol,
  IonGrid,
  IonIcon,
  IonLabel,
  IonRow,
  IonText,
} from "@ionic/react";
import { Box, Stack } from "@mui/material";
import { ShiftContext } from "@src/app/components/workWithFriends/interface";
import {
  fetchNumFamiliarColleaguesAndFriendsShown,
  getNumberOfOpenShiftsInSlot,
} from "@src/app/components/workWithFriends/utils";
import { WorkWithFriends } from "@src/app/components/workWithFriends/workWithFriends";
import { PreviouslyUnassignedAlert } from "@src/app/dayView/Alerts";
import CountDownTimer from "@src/app/dayView/shiftItem/CountDownTimer";
import { MINIMUM_TIME_FOR_MANDATORY_BREAK_IN_HOURS } from "@src/app/hcpShifts/constants";
import { useWorkerQualifications } from "@src/app/licenseManager/hooks/useWorkerQualifications";
import { useIsBreakPolicyAcknowledgementRequired } from "@src/app/mandatoryBreakPolicy/hooks/useIsBreakPolicyAcknowledgementRequired";
import { UrgencyType } from "@src/app/openShifts/urgentShifts/constants";
import { NegotiationStatus } from "@src/app/rateNegotiation/interfaces";
import {
  BlockedShiftButton,
  BookShiftButton,
  LocationPermissionsButton,
  UndeterminedShiftButton,
} from "@src/app/ShiftAction";
import { useAppSelector } from "@src/app/store";
import { useDefinedAgent } from "@src/app/store/helperHooks";
import { isShiftPriorityValid } from "@src/app/utils/shiftHelper";
import { getShiftFacilityDistanceText } from "@src/app/utils/shiftHelper";
import { FACILITY_VIDEO_USER_EVENTS, TRACK_EVENT_SCREENS } from "@src/app/videoPlayer/constants";
import { FullScreenVideoPlayer } from "@src/app/videoPlayer/FullScreenVideoPlayer";
import { AgentRole } from "@src/appV2/Agents";
import { useIsExtraTimePayEnabled } from "@src/appV2/Facilities/api/useIsExtraTimePayEnabled";
import { ExtraTimePayBottomSheet } from "@src/appV2/Facilities/components/ExtraTimePayBottomSheet";
import { CbhFeatureFlag, useCbhFlags } from "@src/appV2/FeatureFlags";
import { isCapacitorPlatform } from "@src/appV2/lib";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useDeviceGeoLocationIfAllowed } from "@src/appV2/Location";
import { BookabilityDecision, BookabilityUnmetCriteria } from "@src/appV2/OpenShifts/ShiftAction";
import { useCheckPolicyAcknowledgement } from "@src/appV2/Shifts/MandatoryBreakPolicy/api/useCheckPolicyAcknowledgement";
import { usePostPolicyAcknowledgement } from "@src/appV2/Shifts/MandatoryBreakPolicy/api/usePostPolicyAcknowledgement";
import { MandatoryBreakPolicyDialog } from "@src/appV2/Shifts/MandatoryBreakPolicy/MandatoryBreakPolicyDialog";
import { NoteAcknowledgementAction } from "@src/appV2/Shifts/MandatoryBreakPolicy/types";
import { HotShiftDialog } from "@src/appV2/Shifts/PayRate/HotShiftDialog";
import { Shift as ShiftV2 } from "@src/appV2/Shifts/Shift/types";
import { useIsQuizWorkersOnWorkplaceRulesFeatureEnabled } from "@src/appV2/WorkplaceQuiz/utils/useIsQuizWorkersOnWorkplaceRulesEnabled";
import { SEARCH_MODE, USER_EVENTS } from "@src/constants";
import useOnScreen from "@src/hooks/useOnScreen";
import { Facility, FacilityTypes, Shift } from "@src/lib/interface";
import {
  FULL_ZONE_NAMES,
  getHumanReadableTime,
  getOffsetFormattedTextBetweenTimezones,
  minutesToHm,
  timeRangeFormatter,
} from "@src/lib/utils";
import { PlayerEventType, PlayerState } from "amazon-ivs-player";
import {
  homeOutline as homeOutlineIcon,
  locationOutline as locationOutlineIcon,
  stopwatchOutline as stopwatchOutlineIcon,
  videocamOutline as videoCamOutlineIcon,
} from "ionicons/icons";
import moment from "moment-timezone";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { LunchBreakInformationDialog } from "./components/MandatoryLunchBreakDialog";
import { RateNegotiationButton } from "./components/RateNegotiation/Button";
import { ShiftDurationWithAction } from "./components/ShiftDurationWithAction";
import { PayRateText } from "./PayRateText";
import { ShiftActionButtonRow } from "./ShiftItemActionButtonRow";
import { ShiftInlineAlerts } from "./ShiftItemInlineAlerts";
import { ShiftItemStatusLine } from "./ShiftItemStatusLine";
import { ShiftPayRate } from "./shiftPayRate";
import { TIME_FORMAT } from "../constants";
import { useMissingDocuments } from "../custom-hooks/useMissingDocuments";
import { ShiftItemViewProps } from "../model";

const ShiftItemView = (props: ShiftItemViewProps) => {
  const {
    shiftDetails,
    onClickOnFacilityDetails,
    isInstantPayAllowed,
    category,
    loadAlertDialog: openBookingAlert,
    isLastMinuteShift,
    submitting,
    showFacilityDetails,
    onItemClick,
    isUrgentShift,
    checkingDocs,
    priorityShift,
    setPriorityShift,
    shiftSlot,
    isRateNegotiationActive,
    updateShiftFields,
    allowedAgentReq,
    searchMode,
    isSingleSourceOfBookabilityEnabled = false,
    shiftBookability,
    updateShiftList,
    isInstantBookingShift: legacyShiftInstantBookStatus,
  } = props;

  const [shift, setShift] = useState<Shift>(shiftDetails);

  const {
    modalIsOpen: mandatoryLunchDialogIsOpen,
    openModal: openMandatoryLunchDialog,
    closeModal: closeMandatoryLunchDialog,
  } = useModalState();
  const extraTimePayBottomSheetModalState = useModalState();
  const previouslyUnassignedAlertState = useModalState();
  const [showFacilityVideo, setShowFacilityVideo] = useState<boolean>(false);
  const hotShiftInfoModalState = useModalState();

  const agent = useDefinedAgent();

  const agentIsBookedForShift = shift.agentId === agent?.userId;
  const agentIsInterestedInShift = isDefined(
    shift?.interested?.find((agentId) => agentId === agent?.userId)
  );

  const isBookingEnabled =
    !agentIsBookedForShift &&
    (!isRateNegotiationActive || shift?.rateNegotiation?.status !== NegotiationStatus.OPEN) &&
    (!shift?.shiftTimeNegotiationActive ||
      (shift?.shiftTimeNegotiationActive &&
        shift?.rateNegotiation?.status !== NegotiationStatus.OPEN));

  const {
    isRequired: isBreakPolicyAcknowledgementRequired,
    noteId: breakPolicyNoteId,
    noteContent: breakPolicyNoteContent,
  } = useIsBreakPolicyAcknowledgementRequired({
    facilityNotes: shift.facility?.facilityNotes ?? [],
    shift,
  });

  const isQuizWorkersOnWorkplaceRulesFeatureEnabled =
    useIsQuizWorkersOnWorkplaceRulesFeatureEnabled({
      workplace: {
        id: shift.facilityId,
        msa: shift?.facility?.fullAddress?.metropolitanStatisticalArea,
        state: shift?.facility?.fullAddress?.state,
        quizWorkersOnRules: shift.facility?.quizWorkersOnRules,
      },
    });

  const mandatoryBreakPolicyDialogState = useModalState();
  const [mandatoryBreakPolicyType, setMandatoryBreakPolicyDialogType] = useState(
    NoteAcknowledgementAction.BOOK_SHIFT
  );

  const {
    mutateAsync: getBreakPolicyAcknowledgements,
    isLoading: isBreakPolicyAcknowledgementsLoading,
  } = useCheckPolicyAcknowledgement();
  const { mutate: postBreakPolicyAcknowledgement } = usePostPolicyAcknowledgement();

  /**
    @deprecated - A very low component (Negotiations) hooks its Dialog opening event
    to an optional callback passed to a very high component, which is being called
    conditionally based on some unexpressive conditions. This is very convoluted and
    needs refactoring.
  */
  const [checksReadyForNegotiationCallback, setChecksReadyForNegotiationCallback] =
    useState<() => void>();

  useEffect(() => {
    setShift(shiftDetails);
  }, [shiftDetails]);

  const workerQualifications = useWorkerQualifications();

  let context = ShiftContext.FACILITY;

  if (showFacilityDetails) {
    if (agentIsBookedForShift) {
      context = ShiftContext.MY_SHIFTS;
    } else {
      context = ShiftContext.CALENDAR;
    }
  }

  const elementRef = useRef<HTMLIonGridElement>(null);
  const isOnScreen = useOnScreen(elementRef, 0.8);
  /**
   * FIXME! moment.fn.zoneName is being redefined here
   * This mutates a global import, which means this can silently affect other callers with
   * no clear visibility about where this functionality came from.
   * Note that this would be changed at runtime, and only when this component renders.
   * It is redefined on each iteration.
   */
  moment.fn.zoneName = function () {
    const abbr = this.zoneAbbr();
    return FULL_ZONE_NAMES[abbr] || abbr;
  };
  const ldFlags = useCbhFlags();

  const recentColleaguesData = useAppSelector((state) => state.recentColleaguesStore);
  const { workers: recentColleagues, loading: recentColleaguesLoading } = recentColleaguesData;
  const friendsData = useAppSelector((state) => state.workerFriendsStore.workerFriends);
  const { data: friends, loading: friendsLoading } = friendsData;

  const facilityTypeIconsEnabledMap: Record<FacilityTypes, boolean> =
    ldFlags[CbhFeatureFlag.FACILITY_TYPE_SHIFT_ICONS] ?? {};
  const isMandatoryLunchBreakInfoPopupEnabled =
    ldFlags[CbhFeatureFlag.ENABLE_LUNCH_BREAK_INFO_POPUP];
  const facilityTypeShiftIconsEnabled = shift.facility?.type
    ? facilityTypeIconsEnabledMap[shift.facility.type] ?? false
    : false;

  const [isViewedEventPublishedAlready, setIsViewedEventPublishedAlready] = useState(false);

  const { pendingDocuments, missingDocuments } = useMissingDocuments(shift);
  const hasMissingDocs = missingDocuments.size > 0;

  const shiftFacilityDistanceText = useMemo(() => {
    return getShiftFacilityDistanceText(shift, agent, isUrgentShift ?? false);
    // only trigger a re-evaluation when the agent.location changes not the full agent object
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shift, agent?.geoLocation, isUrgentShift]);

  const { data: deviceGeolocation } = useDeviceGeoLocationIfAllowed();
  const geolocationIsAvailable = isDefined(deviceGeolocation) || !isCapacitorPlatform();

  const isExtraTimePayEnabled = useIsExtraTimePayEnabled(shift.facility);
  const { isHotShift } = shift;

  const { tmz: facilityTimezone } = shift.facility as Facility;
  const { tmz: agentTimezone } = agent;

  const logVideoEvent = useCallback(
    (eventType: PlayerEventType | PlayerState) => {
      const eventName = FACILITY_VIDEO_USER_EVENTS[eventType];
      if (isDefined(eventName)) {
        logEvent(eventName, {
          facilityId: shift.facilityId,
          screen: TRACK_EVENT_SCREENS.FACILITY_PROFILE,
        });
      }
    },
    [shift.facilityId]
  );

  const policyAcknowledgementEventPayload = {
    shiftId: shift._id,
    facilityId: shift.facility?.userId,
    agentId: agent.userId,
    action: mandatoryBreakPolicyType,
    noteId: breakPolicyNoteId,
  };

  const getShiftTimeInformation = () => {
    if (
      isUrgentShift &&
      (shift.urgency === UrgencyType.NCNS ||
        (shift.urgency === UrgencyType.LATE_CANCELLATION &&
          moment().isSameOrAfter(moment(shift.start)) &&
          shift.extendBookabilityTo))
    ) {
      return `Leave now and work until ${moment(shift.end)
        .tz(String(facilityTimezone))
        .format("MMMM DD, hh:mm A")}`;
    }

    let text = `${timeRangeFormatter(
      TIME_FORMAT,
      {
        start: shift.start as string,
        end: shift.end as string,
      },
      facilityTimezone
    )}`;
    let shiftDuration = "";
    if (shift.time) {
      shiftDuration = getHumanReadableTime(shift.time);
    }
    if (isUrgentShift) {
      text = `${moment(shift.start).tz(String(facilityTimezone)).format("ddd MMMM DD")} ${text}`;
    } else if (isLastMinuteShift) {
      text = `${moment(shift.start).tz(String(facilityTimezone)).format("ddd MMM DD")} ${text}`;
    }
    return (
      <>
        {text}{" "}
        <ShiftDurationWithAction
          shiftDuration={shiftDuration}
          isInfoButtonVisible={
            shift?.facility?.requiresLunchBreak &&
            isMandatoryLunchBreakInfoPopupEnabled &&
            (shift?.time ?? 0) > MINIMUM_TIME_FOR_MANDATORY_BREAK_IN_HOURS
          }
          onInfoButtonClick={() => {
            openMandatoryLunchDialog();
            logEvent(USER_EVENTS.VIEWED_MANDATORY_UNPAID_BREAK_MODAL, {
              shiftId: shift?._id,
              timestamp: new Date(),
            });
          }}
        />
      </>
    );
  };

  const priorityTillDuration = priorityShift
    ? minutesToHm(moment(shift.priorityTill).diff(moment.utc(), "minutes"))
    : { hours: 0, minutes: 0 };

  useEffect(() => {
    if (
      isViewedEventPublishedAlready ||
      !shift ||
      !isDefined(category) ||
      !agent ||
      hasMissingDocs === undefined ||
      friendsLoading ||
      recentColleaguesLoading ||
      !isOnScreen ||
      agentIsBookedForShift
    ) {
      return;
    }
    const { numFamiliarColleaguesShown, numFriendsShown } =
      fetchNumFamiliarColleaguesAndFriendsShown(
        recentColleagues,
        friends,
        shiftSlot?.bookedBy ?? []
      );
    logEvent(USER_EVENTS.OPEN_SHIFT_VIEWED, {
      start: shift?.start,
      end: shift?.end,
      facility_id: shift?.facilityId,
      shift_id: shift?.shiftId,
      worker_msa: agent.address?.metropolitanStatisticalArea,
      hourly_pay: shift?.pay,
      agent_req: shift?.agentReq,
      shift_type: shift?.name,
      num_open_shifts_in_slot: getNumberOfOpenShiftsInSlot(shiftSlot?.stats ?? {}),
      num_colleagues_shown: shiftSlot?.bookedBy?.length,
      has_missing_documents: hasMissingDocs,
      has_profile_picture_uploaded: !!agent.isProfilePictureUploaded,
      worker_is_hidden_as_colleague_on_shifts: !!agent.hideAsColleagueOnShifts,
      num_familiar_colleagues_shown: numFamiliarColleaguesShown ?? 0,
      context,
      num_friends_shown: numFriendsShown ?? 0,
      hasExtraTimePay: isExtraTimePayEnabled,
      isPriorityAccessShift: shift.priorityTill ? isShiftPriorityValid(shift.priorityTill) : false,
      priorityAccessTimeRemaining: shift.priorityTill
        ? moment.utc().diff(moment(shift.priorityTill), "minutes")
        : 0,
      isCovidHazardPayActive: Boolean(shift.facility?.hazardCharge?.covid),
      facilityDistance: shift.facility?.distance,
      isHolidayShift: shift.isHolidayShift,
      isInstantPay: shift.isInstantPay,
      isHotShift,
      isLastMinuteShift,
      workplaceMSA: shift.facility?.fullAddress?.metropolitanStatisticalArea,
      distance: shift.distance,
      agentId: agent.userId,
      legacyShiftInstantBookStatus,
      legacyShiftBookabilityCategory: category,
      shiftBookability: shiftBookability ?? {},
    });
    setIsViewedEventPublishedAlready(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    recentColleaguesLoading,
    shiftSlot,
    shift,
    hasMissingDocs,
    agent,
    friendsLoading,
    isOnScreen,
    category,
  ]);

  useEffect(() => {
    if (
      (isRateNegotiationActive || shift?.shiftTimeNegotiationActive) &&
      shift._id &&
      !shift?.rateNegotiation
    ) {
      logEvent(USER_EVENTS.NEGOTIABLE_SHIFT_FOUND, {
        shiftId: shift._id,
        context: SEARCH_MODE.DATE,
      });
    }
  }, [shift, isRateNegotiationActive]);

  const facilityVideoLink = shift.facility?.videos?.[0]?.url;

  const agentReqTagText = useMemo(() => {
    if (allowedAgentReq?.length && workerQualifications?.length) {
      if (
        shift?.agentReq &&
        (allowedAgentReq.includes("all") || allowedAgentReq.includes(shift.agentReq)) &&
        workerQualifications.includes(shift.agentReq)
      ) {
        return shift.agentReq;
      } else if (
        shift?.agentReq === AgentRole.NURSE &&
        (allowedAgentReq.includes("all") || allowedAgentReq.includes(shift.agentReq)) &&
        (workerQualifications.includes("LVN") || workerQualifications.includes("RN"))
      ) {
        return agent?.preference?.qualification ?? "";
      }
    }
    return "";
  }, [shift?.agentReq, allowedAgentReq, workerQualifications, agent?.preference?.qualification]);

  const startBookingChecks = async (
    bookingActionType: NoteAcknowledgementAction,
    callback?: () => void
  ) => {
    if (isBreakPolicyAcknowledgementRequired) {
      const existingAcknowledgements = await getBreakPolicyAcknowledgements({
        noteId: breakPolicyNoteId,
        policyAcknowledgementAction: bookingActionType,
      });

      if (existingAcknowledgements?.data.length === 0) {
        logEvent(
          APP_V2_USER_EVENTS.MANDATORY_BREAK_POLICY_VIEWED,
          policyAcknowledgementEventPayload
        );
        mandatoryBreakPolicyDialogState.openModal();
        return;
      }
    }

    // When this is:
    // - called with a function, it will only do the bookability checks and call the callback
    // - not called with a function, it will do the checks and open the booking confirmation dialog
    // This behavior needs to be refactored
    openBookingAlert(callback);
  };

  return (
    <>
      {isBreakPolicyAcknowledgementRequired ? (
        <MandatoryBreakPolicyDialog
          modalState={mandatoryBreakPolicyDialogState}
          contentLines={breakPolicyNoteContent.split("\n")}
          acknowledgementAction={mandatoryBreakPolicyType}
          onClose={() => {
            logEvent(
              APP_V2_USER_EVENTS.MANDATORY_BREAK_POLICY_CANCELLED,
              policyAcknowledgementEventPayload
            );
          }}
          onContinue={() => {
            logEvent(
              APP_V2_USER_EVENTS.MANDATORY_BREAK_POLICY_ACCEPTED,
              policyAcknowledgementEventPayload
            );
            postBreakPolicyAcknowledgement({
              policyAcknowledgementAction: mandatoryBreakPolicyType,
              noteId: breakPolicyNoteId,
            });

            // This is a workaround to solve the convoluted logic in the RateNegotiation component
            // that requires only a part of bookability checks to be done before opening the negotiation
            // openBookingAlert (loadAlertDialog) is:
            // - doing all the checks and starting the booking confirmation IF the param passed is not a function
            // - does some of the checks and stops calling the callback IF the param (callback) passed is a function
            // ⬆️ This is how the logic of "What component to open" is working in the current implementation
            if (
              mandatoryBreakPolicyType === NoteAcknowledgementAction.START_RATE_NEGOTIATION &&
              checksReadyForNegotiationCallback
            ) {
              openBookingAlert(checksReadyForNegotiationCallback);
              return;
            }

            openBookingAlert();
          }}
        />
      ) : null}
      <IonGrid onClick={onItemClick} ref={elementRef}>
        <IonRow className="shift-ion-time-range-text ion-label">
          <IonCol size="12">
            <Box>{getShiftTimeInformation()}</Box>
            {agentTimezone &&
              facilityTimezone &&
              moment(shift.start).tz(agentTimezone).utcOffset() !==
                moment(shift.start).tz(facilityTimezone).utcOffset() && (
                <IonLabel
                  className="ion-text-wrap"
                  style={{
                    background: "#D88C33",
                    borderRadius: "4px",
                    width: "fit-content",
                    padding: "1px 4px",
                    color: "#FFFFFF",
                    fontWeight: 600,
                  }}
                >
                  {`${moment()
                    .tz(String(facilityTimezone))
                    .zoneName()} (${getOffsetFormattedTextBetweenTimezones(
                    shift.start as string,
                    agentTimezone as string,
                    facilityTimezone as string
                  )})`}
                </IonLabel>
              )}
          </IonCol>
        </IonRow>
        {shift?.rateNegotiation?.status !== NegotiationStatus.OPEN && (
          <IonRow>
            <IonCol size="12">
              <PayRateText
                totalPay={
                  <ShiftPayRate shift={shift} isUrgentShift={isUrgentShift} ldFlags={ldFlags} />
                }
                isInstantPay={isInstantPayAllowed}
                isHotShift={isHotShift}
                onHotShiftClick={() => {
                  logEvent(APP_V2_USER_EVENTS.HOT_SHIFT_ICON_TAPPED, {
                    shiftId: shift._id,
                    workerMsa: agent.address?.metropolitanStatisticalArea,
                    agentReq: shift.agentReq,
                    shiftType: shift.name,
                    facilityId: shift.facilityId,
                    agentId: agent.userId,
                  });
                  hotShiftInfoModalState.openModal();
                }}
              />
            </IonCol>
          </IonRow>
        )}

        {showFacilityDetails && shift.isHolidayShift && (
          <IonChip color="dark" className="small-chip">
            <IonLabel>It&apos;s {shift.holidayName}</IonLabel>
          </IonChip>
        )}

        {(Boolean(agentReqTagText) || facilityTypeShiftIconsEnabled) && (
          <IonRow onClick={() => onClickOnFacilityDetails()}>
            <IonCol size="12">
              {Boolean(agentReqTagText) && (
                <IonChip color="primary" className="small-chip small-chip-button">
                  <IonButton fill="clear" disabled size="small" className="tag-type-clip-button">
                    <span className="tag-type-clip-text" data-testid="agent-req-chip">
                      {agentReqTagText}
                    </span>
                  </IonButton>
                </IonChip>
              )}
              {facilityTypeShiftIconsEnabled && (
                <IonChip color="primary" className="small-chip small-chip-button">
                  <IonButton fill="clear" disabled size="small" className="tag-type-clip-button">
                    <span className="tag-type-clip-text">{shiftDetails.facility?.type}</span>
                  </IonButton>
                </IonChip>
              )}
            </IonCol>
          </IonRow>
        )}
        {showFacilityDetails && (
          <IonRow onClick={() => onClickOnFacilityDetails()} role="button">
            <IonCol size="12" className="shift-item__description">
              <IonText>
                <h3
                  className={`shift-item__title-link${
                    facilityTypeShiftIconsEnabled ? " no-margin-top" : ""
                  }`}
                >
                  {shift?.facility?.name}
                </h3>
              </IonText>
              <IonText>
                <p className="address-text">{shift?.facility?.fullAddress?.formatted}</p>
              </IonText>
              <IonRow className="etp-dist-video-wrapper ion-align-items-center">
                {shiftFacilityDistanceText && (
                  <IonText>
                    <p className="distance-text">
                      <IonIcon
                        className="icon distance-icon"
                        icon={isUrgentShift ? locationOutlineIcon : homeOutlineIcon}
                        color={isUrgentShift ? "#828282" : undefined}
                      />
                      {shiftFacilityDistanceText}
                    </p>
                  </IonText>
                )}
                {isExtraTimePayEnabled && (
                  <IonText
                    onClick={(event) => {
                      event.stopPropagation();
                      extraTimePayBottomSheetModalState.openModal();
                      logEvent(USER_EVENTS.VIEWED_EXTRA_TIME_PAY_ALERT, {
                        workerId: agent.userId,
                      });
                    }}
                  >
                    <p className="facility-info-link">
                      <IonIcon size="small" icon={stopwatchOutlineIcon} /> Extra Time Pay Facility
                    </p>
                  </IonText>
                )}
              </IonRow>
            </IonCol>
          </IonRow>
        )}

        {showFacilityDetails && facilityVideoLink && (
          <IonRow className="etp-dist-video-wrapper ion-align-items-center">
            <IonCol size="12">
              <IonText onClick={() => setShowFacilityVideo(true)}>
                <span className="facility-info-link" data-testid="facility-video-link">
                  <IonIcon size="small" icon={videoCamOutlineIcon} />
                  Facility Video
                </span>
              </IonText>
            </IonCol>
          </IonRow>
        )}
        {!agentIsBookedForShift && agentIsInterestedInShift && (
          <IonRow>
            <IonCol size="12" text-center className="shift-item__text-waiting-confirmation">
              Shift Request Sent, Waiting for Confirmation
            </IonCol>
          </IonRow>
        )}
        {!hasMissingDocs && !pendingDocuments.size && shiftSlot && (
          <WorkWithFriends shiftSlot={shiftSlot} shift={shift} context={context} />
        )}
        {showFacilityDetails &&
          (agentIsBookedForShift ? (
            <IonRow>
              <IonCol size="12" className="shift-item__work-confirmed">
                <ShiftItemStatusLine showSentHomeMessage={shift.hasSentHomeRequest} />
              </IonCol>
            </IonRow>
          ) : (
            <ShiftInlineAlerts
              shift={shift}
              interested={agentIsInterestedInShift}
              category={category}
            />
          ))}

        {isBookingEnabled && (
          <>
            {isDefined(shift._id) ? (
              <>
                {isSingleSourceOfBookabilityEnabled &&
                  searchMode !== SEARCH_MODE.NON_QFA_RANGE_SHIFTS && (
                    <Stack justifyContent="center" marginY={2} marginRight={1} spacing={1}>
                      {geolocationIsAvailable ? (
                        <>
                          {/* If shift cannot be booked and is neither due to instant book being disabled
                           * nor quiz being being a requirement, show blocked button
                           */}
                          {isDefined(shiftBookability) &&
                          shiftBookability.decision === BookabilityDecision.BLOCKED &&
                          ![
                            BookabilityUnmetCriteria.SHIFT_INSTANT_BOOK_DISABLED,
                            BookabilityUnmetCriteria.WORKER_HAS_NOT_PASSED_QUIZ_FOR_WORKPLACE_RULES,
                          ].includes(shiftBookability.unmetCriteria?.[0]) ? (
                            <BlockedShiftButton shiftBookability={shiftBookability} shift={shift} />
                          ) : null}

                          {/* If shift can be booked
                           * or is blocked due to instant book being disabled show book button
                           * or is blocked due to quiz requirement
                           */}
                          {isDefined(shiftBookability) &&
                          (shiftBookability.decision === BookabilityDecision.ALLOWED ||
                            shiftBookability.decision === BookabilityDecision.UNDETERMINED ||
                            (shiftBookability.decision === BookabilityDecision.BLOCKED &&
                              (shiftBookability.unmetCriteria?.[0] ===
                                BookabilityUnmetCriteria.SHIFT_INSTANT_BOOK_DISABLED ||
                                (shiftBookability.unmetCriteria?.[0] ===
                                  BookabilityUnmetCriteria.WORKER_HAS_NOT_PASSED_QUIZ_FOR_WORKPLACE_RULES &&
                                  isQuizWorkersOnWorkplaceRulesFeatureEnabled)))) ? (
                            <BookShiftButton
                              shiftBookability={shiftBookability}
                              shift={shift}
                              agentId={agent.userId}
                              isInstantBookingShift={
                                shiftBookability.decision === BookabilityDecision.ALLOWED ||
                                shiftBookability.decision === BookabilityDecision.UNDETERMINED
                              }
                              isUrgentShift={isUrgentShift ?? false}
                              isLastMinuteShift={isLastMinuteShift ?? false}
                              isPriorityShift={priorityShift ?? false}
                              geolocation={deviceGeolocation?.geoLocation}
                              onBookShift={(bookedShift) => {
                                // Manually update the shift state so the booked shift is labelled with "You're working this shift"
                                setShift({ ...shift, agentId: bookedShift.agentId });

                                updateShiftList?.(shift as ShiftV2, true, bookedShift);
                              }}
                            />
                          ) : null}

                          {/* If shift bookability criteria is undetermined, show a static button */}
                          {isDefined(shiftBookability) &&
                          ((shiftBookability.decision === BookabilityDecision.BLOCKED &&
                            shiftBookability.unmetCriteria.length === 0) ||
                            (shiftBookability.decision === BookabilityDecision.BLOCKED &&
                              shiftBookability.unmetCriteria[0] ===
                                BookabilityUnmetCriteria.WORKER_HAS_NOT_PASSED_QUIZ_FOR_WORKPLACE_RULES &&
                              !isQuizWorkersOnWorkplaceRulesFeatureEnabled)) ? (
                            <UndeterminedShiftButton />
                          ) : null}

                          {/* If shift bookability is not defined, show a static button */}
                          {!isDefined(shiftBookability) ? <UndeterminedShiftButton /> : null}
                        </>
                      ) : (
                        <LocationPermissionsButton />
                      )}
                    </Stack>
                  )}

                {!isSingleSourceOfBookabilityEnabled ||
                searchMode === SEARCH_MODE.NON_QFA_RANGE_SHIFTS ? (
                  <ShiftActionButtonRow
                    shift={shift}
                    submitting={Boolean(submitting || isBreakPolicyAcknowledgementsLoading)}
                    onClick={() => {
                      setMandatoryBreakPolicyDialogType(NoteAcknowledgementAction.BOOK_SHIFT);
                      return startBookingChecks(NoteAcknowledgementAction.BOOK_SHIFT);
                    }}
                    category={category}
                    isInterested={agentIsInterestedInShift}
                    documentsAreMissing={hasMissingDocs || Boolean(pendingDocuments.size)}
                    isCheckingDocuments={Boolean(checkingDocs)}
                    isPriorityShift={priorityShift}
                  />
                ) : null}
              </>
            ) : null}
          </>
        )}
        {!agentIsBookedForShift && priorityShift && (
          <IonRow className="ion-align-items-center ion-justify-content-center">
            <IonLabel className="ion-label" color="shift-priority">
              <h4 data-testid="priority-access-counter">
                Priority Access expires in{" "}
                <CountDownTimer
                  hours={priorityTillDuration.hours}
                  minutes={priorityTillDuration.minutes}
                  seconds={0}
                  callbackFn={() => setPriorityShift(false)}
                />
              </h4>
            </IonLabel>
          </IonRow>
        )}

        <RateNegotiationButton
          shift={shift}
          isRateNegotiationActive={Boolean(isRateNegotiationActive)}
          openPreviouslyUnassignedAlert={() => previouslyUnassignedAlertState.openModal()}
          loadAlertDialog={(callback?: () => void) => {
            // We need to use the state updater function to avoid the callback being used as the updater function
            setChecksReadyForNegotiationCallback(() => callback);
            setMandatoryBreakPolicyDialogType(NoteAcknowledgementAction.START_RATE_NEGOTIATION);
            return startBookingChecks(NoteAcknowledgementAction.START_RATE_NEGOTIATION, callback);
          }}
          isLoadingShiftBook={Boolean(
            checkingDocs || submitting || isBreakPolicyAcknowledgementsLoading
          )}
          workApproved={agentIsBookedForShift}
          updateShiftViewRateNegotiation={(updatedShift) => {
            setShift(updatedShift);
            updateShiftFields?.(updatedShift, ["rateNegotiation"]);
          }}
        />
      </IonGrid>
      <ExtraTimePayBottomSheet
        facilityUserId={shift.facility!.userId!}
        modalState={extraTimePayBottomSheetModalState}
      />
      <PreviouslyUnassignedAlert modalState={previouslyUnassignedAlertState} />
      <HotShiftDialog modalState={hotShiftInfoModalState} />
      {shift?.facility?.name && shift.time ? (
        <LunchBreakInformationDialog
          isOpen={mandatoryLunchDialogIsOpen}
          closeModal={closeMandatoryLunchDialog}
          shiftDetails={{
            facilityName: shift?.facility?.name,
            shiftTime: shift.time,
          }}
        />
      ) : null}
      {showFacilityVideo && facilityVideoLink && (
        <FullScreenVideoPlayer
          onClose={() => setShowFacilityVideo(false)}
          onVideoEventListener={logVideoEvent}
          streamUrl={facilityVideoLink}
          title={shift?.facility?.name ?? ""}
        />
      )}
    </>
  );
};

export { ShiftItemView };
